import {
	Button,
	MenuDivider,
	MenuItemCheckbox,
	mergeClasses,
} from "@fluentui/react-components";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useInteractionMenuStyle } from "@/components/molecules/interactionMenu/style";
import { useInteractionStyle } from "@/components/organisms/interaction/style";
import { MenuListPopover } from "@/components/atoms/menuList";
import { Queue } from "@/repository/models";
import { QueuesState, useQueuesStore } from "@/common/stores/nonpersistingStores/queues";
import { ArrowSplitRegular } from "@fluentui/react-icons";
import TooltipWithMount from "@/components/atoms/tooltipWithMount/TooltipWithMount";
import { QueueFilterProps } from "./types";
import {
	AppCacheActions,
	AppCacheState,
	useAppCacheStore,
} from "@/common/stores/persistingStores/app";
import { useQueueFilterStyle } from "./styles";
import { notificationListener } from "@/hooks/notification";
import { ServerEvent } from "@/common/notification";

export const QueueFilter = (props: QueueFilterProps) => {
	const { cacheKey, onSelectedQueuesChange } = props;
	const menuClasses = useInteractionMenuStyle();

	const queueFilterClasses = useQueueFilterStyle();

	const classes = useInteractionStyle();
	const intl = useIntl();
	const queueFilterKey = "queueFilterKey";

	const appStoreCache = useAppCacheStore((state: AppCacheState) => state.cache);

	const appStoreActions = useAppCacheStore((state: AppCacheActions) => state);

	const [selectedQueues, setSelectedQueues] = useState<string[]>(
		useCallback(
			() => appStoreCache[cacheKey] || ([] as string[]),
			[appStoreCache]
		)
	);

	const queueState = useQueuesStore((state: QueuesState) => state);

	const cleanSelectedQueues = useCallback(() => {
		setSelectedQueues([]);
		appStoreActions.setCache(cacheKey, []);
	}, []);

	notificationListener(ServerEvent.UserMembershipRemoved, (queue: Queue) => {
		setSelectedQueues((prevSelected) =>
			prevSelected.filter((id) => id !== queue.id)
		);
	});

	const manageSelectedQueues = useCallback(
		(queueId: string) => {
			let newSelectedQueues = [];
			if (selectedQueues.includes(queueId)) {
				newSelectedQueues = selectedQueues.filter((x) => x !== queueId);
			} else {
				newSelectedQueues = [...selectedQueues, queueId];
			}

			appStoreActions.setCache(cacheKey, newSelectedQueues);
			setSelectedQueues(newSelectedQueues);
		},
		[queueState.queues, selectedQueues]
	);

	useEffect(() => {
		onSelectedQueuesChange(selectedQueues);
	}, [selectedQueues]);

	useEffect(() => {
		if (queueState.isDataLoaded) {
			setSelectedQueues((prevSelected) =>
				prevSelected.filter((id) => queueState.queues.some((queue) => queue.id === id))
			);
		}
	}, [queueState.queues, queueState.isDataLoaded]);

	const filterQueuesItems = useMemo<JSX.Element[]>(
		() => [
			...queueState.queues.map((queue: Queue, index: number) => (
				<TooltipWithMount
					key={`${queue.id}-${index}`}
					positioning={"above"}
					content={queue.name}
					relationship="label"
				>
					<MenuItemCheckbox
						key={`${queue.id}-${index}`}
						name={queueFilterKey}
						value={queue.id}
						className={mergeClasses(
							menuClasses.filterCheckboxItem,
							classes.leftAlign,
							classes.ellipsis
						)}
						onClick={() => {
							manageSelectedQueues(queue.id);
						}}
					>
						{queue.name}
					</MenuItemCheckbox>
				</TooltipWithMount>
			)),
		],
		[queueState.queues, selectedQueues]
	);

	const queueFilter = useMemo<JSX.Element>(
		() => (
			<MenuListPopover
				classesName={{
					menuPopover: mergeClasses(
						menuClasses.menuPopover,
						queueFilterClasses.menuPopover
					),
					menuList: menuClasses.queueFilterPopoverContent,
					menuHeader: menuClasses.filterGroupHeader,
				}}
				checkedValues={{
					[queueFilterKey]: selectedQueues,
				}}
				positioning="above-end"
				trigger={
					selectedQueues.length ? (
						<Button
							shape="circular"
							onClick={(e) => {
								e.preventDefault();
							}}
							className={mergeClasses(
								classes.filterButton,
								menuClasses.filterTypeButton
							)}
						>
							<div className={classes.filterFontWeight}>
								{selectedQueues.length}
							</div>
							<ArrowSplitRegular className={classes.filterButtonIcon} />
						</Button>
					) : (
						<Button
							appearance="transparent"
							icon={<ArrowSplitRegular />}
							onClick={(e) => {
								e.preventDefault();
							}}
							className={classes.filterMargin}
						/>
					)
				}
				itemsHeader={[
					<span>
						{intl.formatMessage({ id: "InteractionMenu.ShowOnlyQueues" })}
					</span>,
					<Button
						appearance="transparent"
						className={menuClasses.cleanButtonSize}
						onClick={() => {
							cleanSelectedQueues();
						}}
					>
						{intl.formatMessage({ id: "InteractionMenu.Reset" })}
					</Button>,
				]}
				itemsDivider={<MenuDivider key="interactionDivider" />}
				items={filterQueuesItems}
			/>
		),
		[queueState.queues, selectedQueues]
	);

	return queueFilter;
};
